import { AlertIssue } from '../store/types';
import { FieldMapping, mapFieldRefToSelector } from './fieldMapper';

export interface PhaseOrderEntry {
  id: string;
  phaseName: string;
}

export interface ScrollWinner {
  issue: AlertIssue;
  mapping: FieldMapping;
  phaseId?: string; // set when mapping.section is 'general-info' or 'milestones' for a phased moment
}

/**
 * Resolves which single active alert issue should receive the initial scroll-on-open, based on
 * the moment's actual visual section order. Must be called with the IDENTICAL activeIssues +
 * phases inputs from every consumer (useFieldHighlight, the phased General-Info/Milestones
 * renderer, FeaturesContainer) so they all agree on the same winner without needing to
 * prop-thread the result between them — only the phases array itself needs threading.
 *
 * Visual priority order (confirmed with Pooja):
 * - Phased moments: for each phase, in display order — General Info, then Milestones for that
 *   phase — then, after ALL phases: Overview, then (at the very end) Features.
 * - Non-phased moments: Milestones, then Overview, then Features.
 *
 * Normalization mirrors `SlideOutPanel/index.tsx`'s `expandSectionForFieldRef` phase-name
 * comparison (`.toLowerCase().replace(/[\s_-]/g, '')`) so this resolver agrees with the existing
 * phase-matching used elsewhere for expanding sections.
 */
export function resolveAlertScrollWinner(
  activeIssues: AlertIssue[],
  phases: PhaseOrderEntry[] // pass [] for non-phased moments
): ScrollWinner | null {
  const mapped = activeIssues
    .map(issue => ({
      issue,
      mapping: mapFieldRefToSelector(issue.fieldRef, issue.ruleId)
    }))
    .filter(
      (x): x is { issue: AlertIssue; mapping: FieldMapping } => !!x.mapping
    );

  const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');

  const findFirst = (
    section: FieldMapping['section'],
    phase?: PhaseOrderEntry
  ) => {
    return mapped.find(({ mapping }) => {
      if (mapping.section !== section) return false;
      if (!phase) return true;
      if (!mapping._phaseNameVariants) return false;
      const phaseNorm = normalize(phase.phaseName);
      return mapping._phaseNameVariants.some(v => normalize(v) === phaseNorm);
    });
  };

  if (phases.length > 0) {
    for (const phase of phases) {
      const gi = findFirst('general-info', phase);
      if (gi) return { ...gi, phaseId: phase.id };
      const ms = findFirst('milestones', phase);
      if (ms) return { ...ms, phaseId: phase.id };
    }
  } else {
    const ms = findFirst('milestones');
    if (ms) return ms;
  }

  const ov = findFirst('overview');
  if (ov) return ov;

  const ft = findFirst('features');
  if (ft) return ft;

  return null;
}
